/** The Wobbly Lunch Express — Explorers 6–7; original child evidence, real kit. */
const FOOD_DELIVERY_PAGES = {
  "fdr-prelude": {
    "step_id": "fdr-prelude",
    "title": "The Lunch Delivery Mystery",
    "subtitle": "Find the delivery problem and make a first prediction",
    "phase": "Prelude",
    "story": "Find the delivery problem and make a first prediction",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "The Lunch Delivery Mystery",
      "say": "Find the delivery problem and make a first prediction",
      "do_now": "Find the delivery problem and make a first prediction",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-story-video",
        "master_teacher_watch": {"button": "Watch the lunch mystery", "why": "Chef Nori needs help! Watch for the problem, then get ready to investigate.", "after": "What went wrong at the café? Your ideas come next."},
        "type": "story_prelude",
        "eyebrow": "A LITTLE CAFÉ, A BIG PROBLEM",
        "title": "The Lunch That Would Not Stay Put",
        "prompt": "Watch the mystery or read the story. We do not know the solution yet.",
        "video": {
          "title": "The Lunch That Would Not Stay Put",
          "path": "food-delivery-robot/prelude/story_v1.mp4",
          "poster": "food-delivery-robot/prelude/story_poster.jpg",
          "caption": "Meet Little Sprout Café and its unsolved delivery mystery."
        },
        "reading": {
          "tab_label": "Read the mystery",
          "eyebrow": "LITTLE SPROUT CAFÉ",
          "title": "Who Can Deliver Lunch?",
          "content": [
            {
              "type": "paragraph",
              "text": "Chef Nori packed a tiny pretend lunch. A picnic table is waiting at the end of a straight, flat lane."
            },
            {
              "type": "paragraph",
              "text": "How could a moving helper carry the parcel? Might it slide, tilt or stay put? Nobody has tested an idea yet."
            },
            {
              "type": "question",
              "text": "What would you look for on the very first try?"
            }
          ]
        }
      },
      {
        "id": "fdr-first-risk",
        "type": "choice_cards",
        "title": "What might happen?",
        "prompt": "Choose your first idea. There is no answer yet.",
        "save_as": "first_risk",
        "required": true,
        "choices": [
          {
            "id": "slide",
            "label": "The lunch might slide"
          },
          {
            "id": "tip",
            "label": "The helper might tip"
          },
          {
            "id": "stay",
            "label": "It might stay on"
          },
          {
            "id": "unsure",
            "label": "I am not sure"
          }
        ]
      },
      {
        "id": "fdr-swan-prelude",
        "type": "saras_checkpoint",
        "title": "Ask Swan about the café mystery",
        "suggested_questions": [
          "What should I notice first?",
          "What is a prediction?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. Before Explore, do not reveal the assembled model or explain its motor."
      },
      {
        "id": "fdr-prelude-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Choose what you think might happen.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "first_risk",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "fdr-wonder": {
    "step_id": "fdr-wonder",
    "title": "What Might Go Wrong?",
    "subtitle": "Choose what to investigate without assuming an answer",
    "phase": "Wonder",
    "story": "Choose what to investigate without assuming an answer",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "What Might Go Wrong?",
      "say": "Choose what to investigate without assuming an answer",
      "do_now": "Choose what to investigate without assuming an answer",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-problem",
        "type": "story_hook",
        "title": "Our engineering job",
        "body": "Our tiny helper must move along the clear lane. The package must have a fair chance to stay on the tray. The robot cannot think, steer itself, or avoid obstacles."
      },
      {
        "id": "fdr-criterion",
        "type": "choice_cards",
        "title": "Which result should we look for?",
        "prompt": "Which would be useful evidence that the test worked?",
        "save_as": "success_criterion",
        "required": true,
        "choices": [
          {
            "id": "reached",
            "label": "It reached the end with the package"
          },
          {
            "id": "halfway",
            "label": "It traveled partway"
          },
          {
            "id": "moved",
            "label": "It moved even if the package fell"
          },
          {
            "id": "unsure",
            "label": "I want to investigate"
          }
        ]
      },
      {
        "id": "fdr-load-prediction",
        "type": "choice_cards",
        "title": "Predict the first delivery",
        "prompt": "What do you think will happen with a very light pretend package?",
        "save_as": "cargo_prediction",
        "required": true,
        "choices": [
          {
            "id": "stable",
            "label": "It will stay put"
          },
          {
            "id": "slide",
            "label": "It may slide off"
          },
          {
            "id": "tilt",
            "label": "The robot may tilt"
          },
          {
            "id": "unknown",
            "label": "I need to test"
          }
        ]
      },
      {
        "id": "fdr-swan-wonder",
        "type": "saras_checkpoint",
        "title": "Ask Swan to help design a test",
        "suggested_questions": [
          "How can I tell a guess from a test?",
          "What should I keep the same?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. Do not propose an untested cargo holder as the correct solution."
      },
      {
        "id": "fdr-wonder-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Choose a goal and first prediction.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "success_criterion",
            "condition": "answered"
          },
          {
            "field": "cargo_prediction",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "fdr-explore": {
    "step_id": "fdr-explore",
    "title": "Meet the Tiny Robot Parts",
    "subtitle": "Investigate the real pieces and how they might help",
    "phase": "Explore",
    "story": "Investigate the real pieces and how they might help",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Meet the Tiny Robot Parts",
      "say": "Investigate the real pieces and how they might help",
      "do_now": "Investigate the real pieces and how they might help",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-kit-story",
        "type": "story_hook",
        "title": "Find your real parts",
        "body": "With batteries removed, compare the photos with the parts in your box. The metal arm on the battery holder is a real switch. The yellow motor has two thin pierced copper terminals; they are delicate."
      },
      {
        "id": "fdr-real-parts",
        "type": "image_gallery",
        "title": "Look at the real parts",
        "prompt": "Find the long orange pieces, blue tray, yellow motor, two wheels, battery holder and the small metal brackets. These are actual kit photos.",
        "images": [
          {
            "id": "kit-a",
            "label": "Pieces laid out",
            "path": "food-delivery-robot/references/parts_overview.jpg",
            "alt": "Unassembled Food Delivery Robot parts"
          },
          {
            "id": "kit-b",
            "label": "Motor and connector tabs",
            "path": "food-delivery-robot/references/motor_terminals.jpg",
            "alt": "Yellow motor with delicate metal wire tabs"
          },
          {
            "id": "kit-c",
            "label": "Battery holder with lever",
            "path": "food-delivery-robot/references/battery_switch.jpg",
            "alt": "Two AA battery holder showing metal contact switch"
          }
        ]
      },
      {
        "id": "fdr-found-parts",
        "type": "choice_cards",
        "title": "Can you find these pieces?",
        "prompt": "Point to the tray, motor and battery holder in your real kit.",
        "save_as": "parts_found",
        "required": true,
        "choices": [
          {
            "id": "found",
            "label": "I found all three"
          },
          {
            "id": "help",
            "label": "I found them with help"
          },
          {
            "id": "missing",
            "label": "A piece may be missing"
          }
        ]
      },
      {
        "id": "fdr-part-job",
        "type": "choice_cards",
        "title": "What does the motor do?",
        "prompt": "Make a prediction about one part.",
        "save_as": "motor_job_prediction",
        "required": true,
        "choices": [
          {
            "id": "wheels",
            "label": "Helps turn the wheels"
          },
          {
            "id": "tray",
            "label": "Holds the food"
          },
          {
            "id": "switch",
            "label": "Makes the lever longer"
          },
          {
            "id": "unsure",
            "label": "I am not sure yet"
          }
        ]
      },
      {
        "id": "fdr-swan-explore",
        "type": "saras_checkpoint",
        "title": "Ask Swan about the parts",
        "suggested_questions": [
          "Where are the copper tabs?",
          "Which piece becomes the tray?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. The motor is not yet assembled and battery must stay removed."
      },
      {
        "id": "fdr-explore-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Find the parts and save your motor idea.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "parts_found",
            "condition": "answered"
          },
          {
            "field": "motor_job_prediction",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "fdr-understand": {
    "step_id": "fdr-understand",
    "title": "What Makes the Wheels Turn?",
    "subtitle": "Explain battery, metal switch, motor and movement",
    "phase": "Understand",
    "story": "Explain battery, metal switch, motor and movement",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "What Makes the Wheels Turn?",
      "say": "Explain battery, metal switch, motor and movement",
      "do_now": "Explain battery, metal switch, motor and movement",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-energy-question",
        "type": "story_hook",
        "title": "How can a switch wake a robot?",
        "body": "A switch makes or breaks the conducting connection. The battery provides energy; the motor turns the two wheels. Before testing, keep the metal lever OPEN/OFF."
      },
      {
        "id": "fdr-power-video",
        "master_teacher_watch": {"button": "Watch what makes wheels turn", "why": "This clue will help you build your own moving lunch helper. Watch now, then try the real kit.", "after": "What starts the motor? You get to predict before switching on."},
        "type": "local_video",
        "title": "How Do the Wheels Begin to Turn?",
        "path": "food-delivery-robot/understand/power_to_motion_v1.mp4",
        "poster": "food-delivery-robot/understand/power_poster.jpg",
        "mission": "Watch after identifying the parts. Think about the metal lever touching its spring."
      },
      {
        "id": "fdr-switch-idea",
        "type": "choice_cards",
        "title": "When might the motor run?",
        "prompt": "What do you predict when the metal lever touches the spring?",
        "save_as": "switch_prediction",
        "required": true,
        "choices": [
          {
            "id": "closed",
            "label": "The electrical path closes"
          },
          {
            "id": "open",
            "label": "The path opens"
          },
          {
            "id": "unsure",
            "label": "Not sure yet"
          }
        ]
      },
      {
        "id": "fdr-power-chain",
        "type": "sequence_builder",
        "title": "Put the motion story in order",
        "prompt": "Build a cause-and-effect chain, then check your idea.",
        "save_as": "energy_chain",
        "shuffle_items": true,
        "items": [
          {
            "id": "battery",
            "label": "Battery and closed metal switch"
          },
          {
            "id": "motor",
            "label": "Motor shafts turn"
          },
          {
            "id": "wheels",
            "label": "Wheels can roll on the surface"
          }
        ],
        "correct_order": [
          "battery",
          "motor",
          "wheels"
        ],
        "correct_feedback": "These parts work as a system.",
        "retry_feedback": "What must happen before the motor can rotate?"
      },
      {
        "id": "fdr-swan-understand",
        "type": "saras_checkpoint",
        "title": "Ask Swan about power and motion",
        "suggested_questions": [
          "What does the metal lever do?",
          "Why does the robot need a motor?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. Can now explain circuit and motor-to-wheel motion, but not claim the real wiring is safe."
      },
      {
        "id": "fdr-understand-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Choose the lever prediction and put the energy chain in order.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "switch_prediction",
            "condition": "answered"
          },
          {
            "field": "energy_chain",
            "condition": "sequence_correct"
          }
        ]
      }
    ]
  },
  "fdr-apply": {
    "step_id": "fdr-apply",
    "title": "Build a Moving Helper",
    "subtitle": "Assemble, inspect and test the real robot safely",
    "phase": "Apply",
    "story": "Assemble, inspect and test the real robot safely",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Build a Moving Helper",
      "say": "Assemble, inspect and test the real robot safely",
      "do_now": "Assemble, inspect and test the real robot safely",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "You recorded assembly checks for your real robot. That does not confirm the motor works yet; we will predict and test movement separately.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-assemble-safety",
        "type": "story_hook",
        "title": "Get ready to build your lunch robot",
        "body": "Keep the batteries out and the switch OFF while building. The 3D guide is next; replay each step as you assemble your real parts. Ask Professor Swan if something will not fit. Handle delicate motor terminals gently; stop if a part looks damaged or sharp.",
        "master_teacher_briefing": {
          "title": "Robot workshop starts now — listen first!",
          "steps": [
            "Lay out your real robot parts. Keep the batteries OUT and the metal switch OFF.",
            "Next, open the 3D guide. Watch a step, try it with the real parts, and replay whenever you need.",
            "If something does not fit, ask Professor Swan. Do NOT turn on the robot yet; first we will finish the build and make a prediction."
          ],
          "spoken": "Your robot workshop starts now! Lay out your real parts, with the batteries out and the switch off. Next, open our 3D guide. Watch one step, try it with your real parts, and replay whenever you want. If something does not fit, tell me and we will investigate together. Do not switch on the robot yet."
        }
      },
      {
        "id": "fdr-assembly-pwo",
        "type": "pwo_asset",
        "title": "Build with the rotating 3D guide",
        "mission": "Open the clean 3D assembly guide. Drag to turn, tap Replay if needed, and compare the animation with your actual parts. Keep batteries removed while fastening screws and wires. The animation does not certify your physical build.",
        "asset": {
          "id": "fdr-threejs-attach_cross_plate",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": "wonderlabs_food_delivery_robot_threejs",
            "scene_name": "attach_cross_plate"
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1
          }
        }
      },
      {
        "id": "fdr-build-check",
        "type": "build_checklist",
        "title": "Build your real robot, then check it",
        "prompt": "Do the REAL build now, using the 3D guide again whenever needed. For each item choose I checked it, Still building, or I need help. Swan can help you investigate without pretending the model proves the real robot works.",
        "master_teacher_briefing": {
          "title": "Your turn to build! Listen before checking",
          "steps": [
            "Keep the switch OFF and batteries out while building. Compare each real part with the 3D guide; replay a step whenever you need.",
            "Now put the real robot together. Take your time. If a part doesn't fit, choose I need help and ask Professor Swan.",
            "Only after you check each part on YOUR robot, choose I checked it. Next we'll predict what happens when we turn it on."
          ],
          "spoken": "Now it's your turn to build the real robot! Keep the batteries out. Match your parts to the 3D guide, and take your time. If something doesn't fit, tell me what happened and we'll investigate it together. When you've checked your real robot, tell me what is ready and what still needs help. We haven't tested its movement yet."
        },
        "statuses": [
          { "id": "complete", "label": "I checked it" },
          { "id": "help", "label": "I need help" },
          { "id": "not_yet", "label": "Still building" }
        ],
        "save_as": "assembly_check",
        "items": [
          {
            "id": "tray",
            "label": "Blue tray sits sturdy and level when the robot stands on its wheels",
            "help": "A tilted tray could let lunch slide and may upset balance. Check alignment with batteries out."
          },
          {
            "id": "mount",
            "label": "Yellow motor is firmly seated between tray and lower orange strip; wheels touch the surface",
            "help": "The motor body is above the ground, the wheels roll, and the bottom orange strip may touch the surface. If it resists, check its orientation instead of forcing it; tell Swan what happened."
          },
          {
            "id": "wires",
            "label": "I checked that the red and black leads are snug on separate pierced motor tabs",
            "help": "Twist wires snugly with batteries removed, gently. Do not bend or break copper tabs."
          },
          {
            "id": "switch",
            "label": "Power lever is OPEN/OFF and test lane is clear",
            "help": "Lever touches the spring when CLOSED. It can start the motor immediately. Keep hands away from wheels when testing."
          }
        ]
      },
      {
        "id": "fdr-motion-predict",
        "master_teacher_response_kind": "prediction",
        "type": "choice_cards",
        "title": "First motion prediction",
        "prompt": "After checking your build, what do you think will happen when the switch is closed?",
        "save_as": "motion_prediction",
        "required": true,
        "choices": [
          {
            "id": "roll",
            "label": "The wheels will move the robot"
          },
          {
            "id": "spin",
            "label": "The motor may turn but robot stay still"
          },
          {
            "id": "no",
            "label": "Nothing may move"
          },
          {
            "id": "unsure",
            "label": "Not sure yet"
          }
        ]
      },
      {
        "id": "fdr-motion-test",
        "master_teacher_response_kind": "reported_trial_result",
        "master_teacher_briefing": {"title": "Your first motion test — listen before switching ON", "steps": ["Keep the lever OFF. Predict what the wheels will do before starting.", "Set the robot on a clear, flat space. Make one short test and switch OFF.", "Tell Swan what really happened—even if nothing moved."], "spoken": "Ready for the first movement test? Keep the switch OFF. First predict what will happen. Use a clear flat space, make one short test and switch OFF. Tell me what you actually saw—even if the robot did not move."},
        "type": "experiment_record",
        "title": "What really happened?",
        "prompt": "Place the robot on a clear flat lane, briefly close the metal lever, watch, then switch OFF. Do not hold moving wheels or touch wiring while powered. Record only what you saw.",
        "save_as": "motion_test",
        "fields": [
          {
            "id": "motion",
            "type": "choice",
            "label": "Did it move?",
            "choices": [
              {
                "id": "moved",
                "label": "It traveled"
              },
              {
                "id": "spun",
                "label": "Wheels turned but it stayed there"
              },
              {
                "id": "still",
                "label": "Nothing moved"
              },
              {
                "id": "unsafe",
                "label": "We stopped for safety"
              },
              {
                "id": "not_tested",
                "label": "Not tested today"
              }
            ]
          },
          {
            "id": "wheel_contact",
            "type": "choice",
            "label": "What did the wheels do?",
            "choices": [
              {
                "id": "rolled",
                "label": "Rolled on the ground"
              },
              {
                "id": "spun",
                "label": "Spun without moving it"
              },
              {
                "id": "not",
                "label": "Did not turn"
              },
              {
                "id": "unknown",
                "label": "I could not tell"
              }
            ]
          }
        ]
      },
      {
        "id": "fdr-master-teacher-diagnostic",
        "type": "master_teacher_diagnostic",
        "title": "Investigate with Professor Swan",
        "prompt": "If something did not work, Swan can help you check one clue at a time. A failed test is a discovery, not a wrong answer.",
        "save_as": "fdr_diagnostic_state",
        "kit": "Food Delivery Robot",
        "safety": "Keep the switch OFF and batteries removed before adjusting parts. Stop and get hands-on help if a part is damaged, hot or sharp, or an adjustment would need excessive force or contact with moving wheels.",
        "cases": [
          {
            "id": "spins_still",
            "label": "Wheels turn, but the robot stays still",
            "checks": [
              {
                "id": "contact",
                "instruction": "With power OFF, are both wheels touching the floor?"
              },
              {
                "id": "seat",
                "instruction": "With batteries removed, is the yellow motor base firmly seated in the orange mounting slot (3D Step 10b)?"
              },
              {
                "id": "grip",
                "instruction": "On a clear, flat surface, do the wheels look free to roll, without rubbing on orange parts?"
              }
            ]
          },
          {
            "id": "nothing",
            "label": "Nothing moves when we switch ON",
            "checks": [
              {
                "id": "contact",
                "instruction": "With power OFF, is the metal switch arm able to reach its spring contact? Do not force it."
              },
              {
                "id": "holder",
                "instruction": "With batteries removed, does the battery holder look undamaged? If wiring or tabs look damaged, stop and get hands-on help; do not switch on."
              }
            ]
          },
          {
            "id": "cargo",
            "label": "The parcel slides or the robot tips",
            "checks": [
              {
                "id": "level",
                "instruction": "With power OFF, does the blue tray look level when the robot stands on its wheels?"
              },
              {
                "id": "placement",
                "instruction": "Would moving the lightweight pretend parcel nearer the middle change its balance?"
              },
              {
                "id": "one_change",
                "instruction": "What ONE simple safe paper or cardboard change could you test next?"
              }
            ]
          },
          {
            "id": "unsafe",
            "label": "Something looks damaged or unsafe",
            "checks": [
              {
                "id": "stop",
                "instruction": "Leave power OFF and stop handling damaged wires, sharp parts or hot components. Stop and tell a grown-up what looks unsafe; do not power on."
              }
            ]
          }
        ]
      },
      {
        "id": "fdr-swan-apply",
        "type": "saras_checkpoint",
        "title": "Ask Swan about assembly",
        "suggested_questions": [
          "My wheels spin but it stays still—what should I check?",
          "How do I know the motor is seated?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. If the child reports spinning in place, guide them to switch OFF, remove batteries, and inspect motor seating and wheel contact. Never say the 3D animation verifies the real assembly."
      },
      {
        "id": "fdr-apply-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Finish the real checks and record the first movement observation, including no-movement or safe stop.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "assembly_check",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "motion_prediction",
            "condition": "answered"
          },
          {
            "field": "motion_test",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "fdr-test-improve": {
    "step_id": "fdr-test-improve",
    "title": "Can the Lunch Stay Put?",
    "subtitle": "Run two comparable delivery attempts and study the difference",
    "phase": "Test & Improve",
    "story": "Run two comparable delivery attempts and study the difference",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Can the Lunch Stay Put?",
      "say": "Run two comparable delivery attempts and study the difference",
      "do_now": "Run two comparable delivery attempts and study the difference",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-motion-recall",
        "type": "prior_work_summary",
        "title": "Remember what the robot did",
        "items": [
          {
            "source_step_id": "fdr-apply",
            "field": "motion_test",
            "label": "Movement check",
            "field_labels": {
              "motion": "Robot",
              "wheel_contact": "Wheels"
            },
            "value_labels": {
              "moved": "Moved",
              "spun": "Turned, but did not travel",
              "still": "Stayed still",
              "unsafe": "Stopped for safety",
              "not_tested": "Not yet tested",
              "rolled": "Rolled",
              "not": "Did not turn",
              "unknown": "Unclear"
            }
          }
        ]
      },
      {
        "id": "fdr-delivery-test1",
        "master_teacher_response_kind": "reported_trial_result",
        "master_teacher_briefing": {"title": "The first lunch delivery — your discovery begins", "steps": ["Put one tiny pretend parcel on the level blue tray.", "Predict whether it will stay on. Try one short delivery on a clear lane, then switch OFF.", "Record what happened. A falling parcel is useful evidence!"], "spoken": "Let's try one lunch delivery. Put one tiny pretend parcel on the tray. Will it stay there? Make a prediction, then try one short delivery on a clear lane and switch OFF. Did the parcel stay on, slide, or fall? Tell me what you saw."},
        "type": "experiment_record",
        "title": "First lunch delivery",
        "prompt": "Try a single tiny pretend parcel on a flat tray, on the same short clear lane. Keep fingers clear of moving wheels, then switch OFF after the short test. It is okay if cargo falls, the robot does not move, or the test must stop. Record what actually happened.",
        "save_as": "delivery_test1",
        "fields": [
          {
            "id": "result",
            "type": "choice",
            "label": "What happened to the parcel?",
            "choices": [
              {
                "id": "arrived",
                "label": "Reached the end"
              },
              {
                "id": "fell",
                "label": "Slid or fell off"
              },
              {
                "id": "tilted",
                "label": "Tray or robot tilted"
              },
              {
                "id": "no_motion",
                "label": "Robot did not travel"
              },
              {
                "id": "unsafe",
                "label": "We stopped for safety"
              },
              {
                "id": "not_tested",
                "label": "Not tested today"
              }
            ]
          },
          {
            "id": "detail",
            "type": "choice",
            "label": "What did you notice?",
            "choices": [
              {
                "id": "start",
                "label": "It changed at the start"
              },
              {
                "id": "middle",
                "label": "It changed during movement"
              },
              {
                "id": "end",
                "label": "Near the end"
              },
              {
                "id": "nothing",
                "label": "Nothing noticeable"
              },
              {
                "id": "unsure",
                "label": "Not sure"
              }
            ]
          }
        ]
      },
      {
        "id": "fdr-cargo-video",
        "master_teacher_watch": {"button": "Watch why the lunch tumbles", "why": "You recorded a first delivery. Now look for clues before changing your design.", "after": "Choose ONE thing to change. Then predict what might happen."},
        "type": "local_video",
        "title": "Why Did the Lunch Tumble?",
        "path": "food-delivery-robot/test_improve/cargo_investigation_v1.mp4",
        "poster": "food-delivery-robot/test_improve/cargo_poster.jpg",
        "mission": "Watch AFTER recording your own first delivery result. Look for explanations, not a guaranteed fix."
      },
      {
        "id": "fdr-change-plan",
        "master_teacher_response_kind": "planned_change",
        "type": "choice_cards",
        "title": "Choose ONE safe change",
        "prompt": "Change just one thing, keeping the lane and test method the same. If a powered retry is not safe, choose “observe instead.”",
        "save_as": "one_change",
        "required": true,
        "choices": [
          {
            "id": "center",
            "label": "Move the tiny parcel nearer the middle"
          },
          {
            "id": "lighter",
            "label": "Try a lighter pretend parcel"
          },
          {
            "id": "paper",
            "label": "Try a simple flat paper carrier"
          },
          {
            "id": "observe",
            "label": "Observe the existing setup without powering it again"
          }
        ]
      },
      {
        "id": "fdr-change-predict",
        "master_teacher_response_kind": "prediction",
        "type": "choice_cards",
        "title": "Predict the change",
        "prompt": "What might happen after your choice?",
        "save_as": "change_prediction",
        "required": true,
        "choices": [
          {
            "id": "better",
            "label": "The parcel may stay better"
          },
          {
            "id": "same",
            "label": "It may behave the same"
          },
          {
            "id": "worse",
            "label": "It may be less stable"
          },
          {
            "id": "unknown",
            "label": "I need evidence"
          }
        ]
      },
      {
        "id": "fdr-delivery-test2",
        "master_teacher_response_kind": "reported_trial_result",
        "master_teacher_briefing": {"title": "Try one change and compare", "steps": ["With the power OFF, make only your chosen safe change.", "Keep the same lane and parcel whenever possible; try again briefly and switch OFF.", "Compare this result with your first try, including if you could not test safely."], "spoken": "Now we're engineers improving a design! With power OFF, change just one thing. Keep the same short lane and try again briefly if safe. Switch OFF and compare with your first test. What changed?"},
        "type": "experiment_record",
        "title": "Try again and compare",
        "prompt": "Keep everything else the same. Change only your chosen factor. Try a safe repeat or mark why you could not. Do not invent a success.",
        "save_as": "delivery_test2",
        "fields": [
          {
            "id": "result",
            "type": "choice",
            "label": "What really happened the second time?",
            "choices": [
              {
                "id": "arrived",
                "label": "Reached the end"
              },
              {
                "id": "fell",
                "label": "Slid or fell"
              },
              {
                "id": "tilted",
                "label": "Tilted"
              },
              {
                "id": "no_motion",
                "label": "Did not travel"
              },
              {
                "id": "unsafe",
                "label": "We stopped for safety"
              },
              {
                "id": "not_tested",
                "label": "Could not test today"
              }
            ]
          },
          {
            "id": "comparison",
            "type": "choice",
            "label": "Compared with the first try, what changed?",
            "choices": [
              {
                "id": "better",
                "label": "It stayed better"
              },
              {
                "id": "same",
                "label": "About the same"
              },
              {
                "id": "worse",
                "label": "It stayed worse"
              },
              {
                "id": "not_compare",
                "label": "We cannot compare yet"
              }
            ]
          }
        ]
      },
      {
        "id": "fdr-swan-improve",
        "type": "saras_checkpoint",
        "title": "Ask Swan about a fair test",
        "suggested_questions": [
          "Why did it slide?",
          "How do I change one thing at a time?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. Do not reveal a guaranteed correct fix; compare saved trial one/trial two evidence. Failed or unsafe tests are valid reported outcomes."
      },
      {
        "id": "fdr-test-improve-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Record both deliveries (or honestly mark not tested) and your one-change idea.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "delivery_test1",
            "condition": "record_complete"
          },
          {
            "field": "one_change",
            "condition": "answered"
          },
          {
            "field": "change_prediction",
            "condition": "answered"
          },
          {
            "field": "delivery_test2",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "fdr-explain": {
    "step_id": "fdr-explain",
    "title": "Tell the Engineering Story",
    "subtitle": "Explain motion and delivery using actual evidence",
    "phase": "Explain",
    "story": "Explain motion and delivery using actual evidence",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Tell the Engineering Story",
      "say": "Explain motion and delivery using actual evidence",
      "do_now": "Explain motion and delivery using actual evidence",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-compare-trials",
        "type": "prior_work_summary",
        "title": "Your two real delivery results",
        "items": [
          {
            "source_step_id": "fdr-test-improve",
            "field": "delivery_test1",
            "label": "First delivery"
          },
          {
            "source_step_id": "fdr-test-improve",
            "field": "delivery_test2",
            "label": "Second delivery"
          }
        ]
      },
      {
        "id": "fdr-mechanism-chain",
        "type": "sequence_builder",
        "title": "How does the little robot move?",
        "prompt": "Put these steps in order.",
        "save_as": "final_mechanism",
        "shuffle_items": true,
        "items": [
          {
            "id": "power",
            "label": "Two batteries and closed lever"
          },
          {
            "id": "spin",
            "label": "Motor turns the shafts"
          },
          {
            "id": "roll",
            "label": "Wheels touch the ground and may roll"
          }
        ],
        "correct_order": [
          "power",
          "spin",
          "roll"
        ],
        "correct_feedback": "You connected the parts to their jobs.",
        "retry_feedback": "Which part supplies energy first?"
      },
      {
        "id": "fdr-evidence-claim",
        "type": "choice_cards",
        "title": "What does your delivery evidence suggest?",
        "prompt": "Choose a claim you can support from your own two tests. You can say the evidence is still unclear.",
        "save_as": "delivery_explanation",
        "required": true,
        "choices": [
          {
            "id": "placement",
            "label": "Where the parcel sits may matter"
          },
          {
            "id": "motion",
            "label": "Movement and balance matter"
          },
          {
            "id": "fit",
            "label": "Parts fitting securely matters"
          },
          {
            "id": "unclear",
            "label": "We need more evidence"
          }
        ]
      },
      {
        "id": "fdr-swan-explain",
        "type": "saras_checkpoint",
        "title": "Ask Swan to connect evidence",
        "suggested_questions": [
          "How do my two results support my idea?",
          "Why can a motor spin but a robot stay still?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. Do not state delivery was successful unless the child's saved result says so."
      },
      {
        "id": "fdr-explain-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Put the motor system in order and choose an evidence-based claim.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "final_mechanism",
            "condition": "sequence_correct"
          },
          {
            "field": "delivery_explanation",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "fdr-reflect": {
    "step_id": "fdr-reflect",
    "title": "What Changed Your Thinking?",
    "subtitle": "Compare an early prediction with the real tests",
    "phase": "Reflect",
    "story": "Compare an early prediction with the real tests",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "What Changed Your Thinking?",
      "say": "Compare an early prediction with the real tests",
      "do_now": "Compare an early prediction with the real tests",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-original-risk",
        "type": "prior_work_summary",
        "title": "Remember your first guess",
        "items": [
          {
            "source_step_id": "fdr-prelude",
            "field": "first_risk",
            "label": "My first guess",
            "value_labels": {
              "slide": "Lunch might slide",
              "tip": "Helper might tip",
              "stay": "It might stay",
              "unsure": "I did not know"
            }
          }
        ]
      },
      {
        "id": "fdr-thinking-change",
        "type": "choice_cards",
        "title": "Did your idea change?",
        "prompt": "Your test is evidence—even if the robot or parcel did not behave as expected.",
        "save_as": "thinking_changed",
        "required": true,
        "choices": [
          {
            "id": "yes",
            "label": "My idea changed"
          },
          {
            "id": "more",
            "label": "I learned more detail"
          },
          {
            "id": "similar",
            "label": "My idea stayed similar"
          },
          {
            "id": "not_yet",
            "label": "I still want to investigate"
          }
        ]
      },
      {
        "id": "fdr-key-learning",
        "type": "choice_cards",
        "title": "What did you discover for yourself?",
        "prompt": "Choose something you can explain to another person.",
        "save_as": "key_learning",
        "required": true,
        "choices": [
          {
            "id": "fit",
            "label": "How parts have to fit together"
          },
          {
            "id": "switch",
            "label": "How the metal switch starts motion"
          },
          {
            "id": "fair",
            "label": "How to compare two tests fairly"
          },
          {
            "id": "persistence",
            "label": "How to troubleshoot and try again"
          }
        ]
      },
      {
        "id": "fdr-swan-reflect",
        "type": "saras_checkpoint",
        "title": "Ask Swan about your discoveries",
        "suggested_questions": [
          "What is one thing I figured out?",
          "Can you remind me of my first prediction?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. Use saved values; do not automatically praise unobserved success."
      },
      {
        "id": "fdr-reflect-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Look back and name one discovery.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "thinking_changed",
            "condition": "answered"
          },
          {
            "field": "key_learning",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "fdr-grow": {
    "step_id": "fdr-grow",
    "title": "Design the Next Delivery",
    "subtitle": "Transfer the discovery to another engineering challenge",
    "phase": "Grow",
    "story": "Transfer the discovery to another engineering challenge",
    "age_band": "explorers_6_7",
    "self_guided": {
      "title": "Design the Next Delivery",
      "say": "Transfer the discovery to another engineering challenge",
      "do_now": "Transfer the discovery to another engineering challenge",
      "help_prompt": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy.",
      "transition_summary": "Your work is saved.",
      "next_preview": "Next, keep investigating with your own evidence.",
      "materials": [
        "STEM Discovery Lab Food Delivery Robot T-S78 (box marked 6+)",
        "Actual printed assembly instructions",
        "Two AA batteries as specified by the kit",
        "Lightweight pretend parcel and a straight flat test lane",
        "Professor Swan's assembly and troubleshooting guidance"
      ],
      "safety": [
        "Keep batteries removed during assembly and wire adjustments. Align screws gently; protect the thin motor terminal tabs from bending.",
        "For a brief power test, set the robot in a clear, low, flat area. Closing the metal lever may start the motor immediately. Switch off before touching moving wheels.",
        "Use lightweight pretend cargo only. Keep tiny parts and cereal away from mouths and younger children; no hot foods or liquids."
      ],
      "parent_note": "The child leads the building, prediction, experiment and investigation with Professor Swan. Follow the manufacturer's specific safety instructions; offer physical help only when requested or a real hazard requires it."
    },
    "blocks": [
      {
        "id": "fdr-new-delivery",
        "type": "story_hook",
        "title": "A new café challenge",
        "body": "A larger pretend parcel needs a longer trip. What would you test next? You are the engineer; there may be several workable ideas. This is a new question, not another required physical build."
      },
      {
        "id": "fdr-new-plan",
        "master_teacher_response_kind": "planned_investigation",
        "type": "choice_cards",
        "title": "Choose the next investigation",
        "prompt": "What would you like to investigate next time?",
        "save_as": "next_design",
        "required": true,
        "choices": [
          {
            "id": "surface",
            "label": "Try another safe surface"
          },
          {
            "id": "parcel",
            "label": "Try a different lightweight pretend package"
          },
          {
            "id": "holder",
            "label": "Design a cardboard tray insert"
          },
          {
            "id": "compare",
            "label": "Try more repeated deliveries"
          }
        ]
      },
      {
        "id": "fdr-share",
        "type": "choice_cards",
        "title": "What will you tell another engineer?",
        "prompt": "Pick the idea you want to share proudly and accurately.",
        "save_as": "share_discovery",
        "required": true,
        "choices": [
          {
            "id": "observe",
            "label": "Look closely before guessing"
          },
          {
            "id": "test",
            "label": "Try one fair change"
          },
          {
            "id": "fit",
            "label": "Check that parts fit"
          },
          {
            "id": "stop",
            "label": "Stop safely and ask for help when needed"
          }
        ]
      },
      {
        "id": "fdr-swan-grow",
        "type": "saras_checkpoint",
        "title": "Ask Swan about your next idea",
        "suggested_questions": [
          "How could I make my next test fair?",
          "What would a real restaurant robot need beyond our motor?"
        ],
        "hidden_context": "Professor Swan: Talk to a curious 6–7-year-old, one practical action at a time. Use the current phase, this activity, and saved predictions or observations. Ask one useful question only when it helps; otherwise explain or help directly. Give progressively stronger hints. Do not invent physical outcomes or claim the 3D view proves a real build. The kit is labeled for ages 6+. Professor Swan guides the child through ordinary assembly, battery placement and first tests. Do not invent blanket adult-supervision rules. Keep batteries out while wiring; stop and request hands-on help only for actual hazards such as damaged or hot parts, sharp edges, or excessive force. Keep the motor off and batteries removed during adjustments. No autonomous driving, remote, sensors, sound or lights in this toy. This is only a battery-motor-wheel toy. Real autonomous robots would need additional sensing and control not provided."
      },
      {
        "id": "fdr-grow-gate",
        "type": "completion_gate",
        "incomplete_title": "Finish this discovery",
        "complete_title": "Ready for the next part",
        "incomplete_prompt": "Choose your next question and what you learned.",
        "complete_prompt": "Your idea or observation is saved. Continue when ready.",
        "rules": [
          {
            "field": "next_design",
            "condition": "answered"
          },
          {
            "field": "share_discovery",
            "condition": "answered"
          }
        ]
      }
    ]
  }
};
export const getFoodDeliveryRobotMissionPage = (stepId) => FOOD_DELIVERY_PAGES[String(stepId || '')] || null;
export default FOOD_DELIVERY_PAGES;
